import {
    ref,
    set,
    get,
    onValue,
    update,
    remove,
    DataSnapshot,
    runTransaction
} from 'firebase/database';
import { database } from '../lib/firebase.ts';
import { GameRoom, Player, Guess } from '../types/game.ts';
import { normalizeTurkishText } from '../utils/wordle.ts';

export class GameService {
    // Oda oluştur
    static async createRoom(roomCode: string, hostPlayer: Player): Promise<GameRoom> {
        const room: GameRoom = {
            id: roomCode,
            code: roomCode,
            targetWord: '', // Host kelimeyi belirleyecek
            players: [{ ...hostPlayer, isHost: true }],
            gameStatus: 'waiting',
            maxGuesses: 6,
            currentGuessIndex: 0,
            createdAt: new Date().toISOString(),
            hostId: hostPlayer.id,
            wordSubmitted: false
        };

        const roomRef = ref(database, `rooms/${roomCode}`);
        await set(roomRef, room);
        return room;
    }

    // Odaya katıl
    static async joinRoom(roomCode: string, player: Player): Promise<GameRoom> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;

        // Oyuncu zaten var mı kontrol et
        const existingPlayer = room.players.find(p => p.id === player.id);
        if (existingPlayer) {
            return room; // Zaten katılmış
        }

        // Oyuncuyu ekle (guest olarak)
        const updatedPlayers = [...room.players, { ...player, isHost: false }];
        const updatedRoom = {
            ...room,
            players: updatedPlayers,
            updatedAt: new Date().toISOString()
        };

        await set(roomRef, updatedRoom);
        return updatedRoom;
    }

    // Oda durumunu güncelle
    static async updateRoom(roomCode: string, updates: Partial<GameRoom>): Promise<GameRoom> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const updateData = {
            ...updates,
            updatedAt: new Date().toISOString()
        };

        await update(roomRef, updateData);

        // Güncellenmiş odayı döndür
        const snapshot = await get(roomRef);
        return snapshot.val() as GameRoom;
    }

    // Oyuncu tahminini güncelle
    static async updatePlayerGuess(
        roomCode: string,
        playerId: string,
        guesses: Guess[]
    ): Promise<GameRoom> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;
        const playerIndex = room.players.findIndex((p: Player) => p.id === playerId);
        if (playerIndex === -1) {
            throw new Error('Oyuncu bulunamadı');
        }

        const updates: Record<string, unknown> = {
            [`players/${playerIndex}/guesses`]: guesses,
            updatedAt: new Date().toISOString()
        };

        await update(roomRef, updates);

        // Güncellenmiş veriyi döndür
        const refreshed = await get(roomRef);
        return refreshed.val() as GameRoom;
    }

    // Tek istekte tahmin ve oyuncu durumunu birlikte yaz (yarışları azaltır)
    static async commitPlayerGuess(
        roomCode: string,
        playerId: string,
        guesses: Guess[],
        status: { isGameComplete: boolean; hasWon: boolean }
    ): Promise<void> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;
        const playerIndex = room.players.findIndex((p: Player) => p.id === playerId);
        if (playerIndex === -1) {
            throw new Error('Oyuncu bulunamadı');
        }

        const updates: Record<string, unknown> = {
            [`players/${playerIndex}/guesses`]: guesses,
            [`players/${playerIndex}/isGameComplete`]: status.isGameComplete,
            [`players/${playerIndex}/hasWon`]: status.hasWon,
            updatedAt: new Date().toISOString()
        };

        await update(roomRef, updates);
    }

    // Transaction ile aynı anda ekleme: yarış (lost update) sorunlarını önler
    static async appendPlayerGuessTransactional(
        roomCode: string,
        playerId: string,
        newGuess: Guess,
        status: { isGameComplete: boolean; hasWon: boolean }
    ): Promise<void> {
        // Önce oyuncunun index'ini bul
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);
        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }
        const room = snapshot.val() as GameRoom;
        const playerIndex = room.players.findIndex((p: Player) => p.id === playerId);
        if (playerIndex === -1) {
            throw new Error('Oyuncu bulunamadı');
        }

        // Sadece ilgili oyuncu düğümünde transaction çalıştır
        const playerRef = ref(database, `rooms/${roomCode}/players/${playerIndex}`);
        await runTransaction(playerRef, (player: Player | null) => {
            if (!player) return player;
            const existingGuesses = Array.isArray(player.guesses) ? player.guesses : [];
            return {
                ...player,
                guesses: [...existingGuesses, newGuess],
                isGameComplete: status.isGameComplete,
                hasWon: status.hasWon
            } as Player;
        });

        // Güncelleme zamanını oda seviyesinde işaretle (yarışsız basit update)
        await update(roomRef, { updatedAt: new Date().toISOString() });
    }

    // Oda durumunu dinle (real-time)
    static subscribeToRoom(roomCode: string, callback: (room: GameRoom) => void) {
        const roomRef = ref(database, `rooms/${roomCode}`);

        const unsubscribe = onValue(roomRef, (snapshot: DataSnapshot) => {
            if (snapshot.exists()) {
                callback(snapshot.val() as GameRoom);
            }
        });

        return unsubscribe;
    }

    // Oda durumunu al
    static async getRoom(roomCode: string): Promise<GameRoom | null> {
        try {
            const roomRef = ref(database, `rooms/${roomCode}`);
            const snapshot = await get(roomRef);

            if (!snapshot.exists()) {
                return null;
            }

            return snapshot.val() as GameRoom;
        } catch (error) {
            console.error('Oda alınırken hata:', error);
            return null;
        }
    }

    // Host kelimeyi belirler
    static async submitWord(roomCode: string, word: string): Promise<GameRoom> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;

        const updatedRoom = {
            ...room,
            targetWord: normalizeTurkishText(word),
            wordSubmitted: true,
            gameStatus: 'playing' as const,
            updatedAt: new Date().toISOString()
        };

        await set(roomRef, updatedRoom);
        return updatedRoom;
    }

    // Oyuncu bilgilerini güncelle
    static async updatePlayer(roomCode: string, playerId: string, updates: Partial<Player>): Promise<void> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;
        const playerIndex = room.players.findIndex((p: Player) => p.id === playerId);
        if (playerIndex === -1) {
            throw new Error('Oyuncu bulunamadı');
        }

        const partialUpdates: Record<string, unknown> = { updatedAt: new Date().toISOString() };
        Object.entries(updates).forEach(([key, value]) => {
            partialUpdates[`players/${playerIndex}/${key}`] = value as unknown;
        });

        await update(roomRef, partialUpdates);
    }


    // Host çıkış yaptığında odayı sil
    static async deleteRoom(roomCode: string): Promise<void> {
        try {
            const roomRef = ref(database, `rooms/${roomCode}`);
            await remove(roomRef);
            console.log(`Oda silindi: ${roomCode}`);
        } catch (error) {
            console.error('Oda silme hatası:', error);
        }
    }

    // Oda silindiğinde oyuncuları ana ekrana yönlendir
    static async notifyRoomDeleted(roomCode: string): Promise<void> {
        try {
            // Oda silindiğinde oyunculara bildirim gönder
            const notificationRef = ref(database, `notifications/${roomCode}`);
            await set(notificationRef, {
                type: 'room_deleted',
                message: 'Host oyundan ayrıldı. Ana ekrana yönlendiriliyorsunuz.',
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error('Bildirim gönderme hatası:', error);
        }
    }


    // Aboneliği iptal et
    static unsubscribe(subscription: any) {
        if (subscription && typeof subscription === 'function') {
            subscription();
        }
    }
}
